import { useId, useMemo, useState } from "react";
import { Chess } from "@vendor/chess.js";
import { boardStyleById, pieceSetById } from "../../appearance/appearance.ts";
import { useAppearance } from "../../appearance/useAppearance.ts";
import { formatSampleLine } from "../../chess/notation.ts";
import type { OpeningGuide } from "../../content/openingGuides.ts";
import { clashCaption, diagramClash } from "./diagramClash.ts";

type Props = {
  guide: OpeningGuide;
  side: "white" | "black";
  title: string;
  /**
   * "page" is the Theory introduction. "card" is the smaller copy on an
   * opening card: the same diagram, opened on the last move so the opening
   * already looks like itself.
   */
  layout?: "page" | "card";
};

const FILES = "abcdefgh";
const PIECE_NAMES: Record<string, string> = {
  p: "pawn",
  n: "knight",
  b: "bishop",
  r: "rook",
  q: "queen",
  k: "king",
};

/** A teaching diagram, independent of the playable board and its input state. */
export function OpeningInfographic({ guide, side, title, layout = "page" }: Props) {
  const compact = layout === "card";
  const moveCount = guide.steps.length === 4 ? "four" : "three";
  // The Theory page starts at move one. A card opens on its last move, where
  // this opening no longer looks like every other first move. For the Italian
  // and the Quiet Two Knights that last move is White's fourth, after Black
  // has replied, so the two boards show the split.
  const [step, setStep] = useState(compact ? guide.steps.length - 1 : 0);
  const appearance = useAppearance();
  const board = boardStyleById(appearance.board);
  const pieces = pieceSetById(appearance.pieces);
  const id = useId().replace(/:/g, "");
  const ply = step * 2 + (side === "white" ? 1 : 2);
  const position = useMemo(() => {
    const chess = new Chess();
    let lastMove = null;
    for (const san of guide.line.slice(0, ply)) lastMove = chess.move(san);
    if (!lastMove) throw new Error(`Missing introductory move for ${title}`);
    return { chess, lastMove };
  }, [guide, ply, title]);

  // The diagram stops on this side's own move. Move 4 includes the reply to
  // move 3, then that side's next move, and still leaves the answer to move 4
  // off the board. A capture (such as exd5 in the Alapin) stays visible.
  const moveLabel = `${step + 1}${side === "white" ? "." : "…"} ${position.lastMove.san}`;
  const line = formatSampleLine(guide.line.slice(0, ply), 1, "w");
  const lesson = guide.steps[step];
  const squarePoint = (square: string) => {
    const file = FILES.indexOf(square[0]);
    const rank = Number(square[1]) - 1;
    return {
      x: 16 + (side === "white" ? file : 7 - file) * 40,
      y: 16 + (side === "white" ? 7 - rank : rank) * 40,
    };
  };
  const from = squarePoint(position.lastMove.from);
  const to = squarePoint(position.lastMove.to);
  const distance = Math.hypot(to.x - from.x, to.y - from.y);
  const arrowEnd = {
    x: to.x + 20 - ((to.x - from.x) / distance) * 24,
    y: to.y + 20 - ((to.y - from.y) / distance) * 24,
  };
  const boardPieces = position.chess.board().flatMap((row, rank) =>
    row.flatMap((piece, file) => {
      if (!piece) return [];
      const square = `${FILES[file]}${8 - rank}`;
      return [{ square, piece, point: squarePoint(square) }];
    })
  );
  const boardDescription = boardPieces
    .map(
      ({ square, piece }) =>
        `${piece.color === "w" ? "White" : "Black"} ${PIECE_NAMES[piece.type]} on ${square}`
    )
    .join("; ");
  const clash = diagramClash(position.chess, position.lastMove);
  const caption = clashCaption(clash);
  const clashSquares = new Set([...clash.hits, ...clash.checks]);
  const ideaSquares = lesson.focus.filter((square) => !clashSquares.has(square));

  const ideaSentence = ideaSquares.length
    ? ` Gold rings mark the idea squares: ${ideaSquares.join(", ")}.`
    : "";
  const clashSentence = caption ? ` ${caption}.` : "";
  const description = compact
    ? `${line}. ${lesson.title}. ${lesson.explanation}${clashSentence}`
    : `${line}. The green arrow shows ${position.lastMove.from} to ${position.lastMove.to}.${ideaSentence}${clashSentence} ${lesson.explanation} ${boardDescription}.`;

  return (
    <section
      className={compact ? "opening-infographic opening-infographic-card" : "opening-infographic"}
      aria-label={compact ? `${title}, first ${moveCount} moves` : undefined}
      aria-labelledby={compact ? undefined : `${id}-heading`}
    >
      {compact ? null : (
        <div className="opening-infographic-heading">
          <h3 id={`${id}-heading`}>Your first {moveCount} moves</h3>
          <p>Select a move to see its position and purpose.</p>
        </div>
      )}
      <div className="opening-infographic-layout">
        <figure className="opening-diagram">
          <svg
            viewBox="0 0 352 352"
            role="img"
            aria-labelledby={`${id}-title`}
            aria-describedby={`${id}-description`}
          >
            <title id={`${id}-title`}>
              {title}: position after {moveLabel}, viewed as {side === "white" ? "White" : "Black"}
            </title>
            <desc id={`${id}-description`}>{description}</desc>
            <defs>
              <marker
                id={`${id}-arrow`}
                viewBox="0 0 10 10"
                refX="8"
                refY="5"
                markerWidth="3"
                markerHeight="3"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#185b45" stroke="#f2fff6" strokeWidth="0.7" />
              </marker>
            </defs>
            {Array.from({ length: 64 }, (_, index) => {
              const file = index % 8;
              const rank = Math.floor(index / 8);
              return (
                <rect
                  key={index}
                  x={16 + file * 40}
                  y={16 + rank * 40}
                  width="40"
                  height="40"
                  fill={(file + rank) % 2 === 0 ? board.light : board.dark}
                />
              );
            })}
            {[from, to].map((point, index) => (
              <rect
                key={index}
                x={point.x + 2}
                y={point.y + 2}
                width="36"
                height="36"
                className="opening-diagram-moved"
              />
            ))}
            {ideaSquares.map((square) => {
              const point = squarePoint(square);
              return (
                <g key={square}>
                  <circle
                    cx={point.x + 20}
                    cy={point.y + 20}
                    r="17"
                    className="opening-diagram-focus-halo"
                  />
                  <circle
                    cx={point.x + 20}
                    cy={point.y + 20}
                    r="17"
                    className="opening-diagram-focus"
                  />
                </g>
              );
            })}
            {[...clashSquares].map((square) => {
              const point = squarePoint(square);
              return (
                <circle
                  key={square}
                  cx={point.x + 20}
                  cy={point.y + 20}
                  r="16"
                  className="opening-diagram-clash"
                />
              );
            })}
            <line
              x1={from.x + 20}
              y1={from.y + 20}
              x2={arrowEnd.x}
              y2={arrowEnd.y}
              stroke="#f2fff6"
              strokeWidth="8"
              strokeLinecap="round"
            />
            <line
              x1={from.x + 20}
              y1={from.y + 20}
              x2={arrowEnd.x}
              y2={arrowEnd.y}
              stroke="#185b45"
              strokeWidth="5"
              strokeLinecap="round"
              markerEnd={`url(#${id}-arrow)`}
            />
            {boardPieces.map(({ square, piece, point }) => (
              <svg
                key={square}
                x={point.x}
                y={point.y}
                width="40"
                height="40"
                viewBox="0 0 40 40"
                aria-hidden="true"
              >
                <use href={`${pieces.file}#${piece.color}${piece.type}`} />
              </svg>
            ))}
            {clash.captured ? (
              <g className="opening-diagram-capture" aria-hidden="true">
                <line x1={to.x + 26} y1={to.y + 8} x2={to.x + 34} y2={to.y + 16} />
                <line x1={to.x + 34} y1={to.y + 8} x2={to.x + 26} y2={to.y + 16} />
              </g>
            ) : null}
            {Array.from({ length: 8 }, (_, index) => (
              <g key={index} className="opening-diagram-coordinate" aria-hidden="true">
                <text x={36 + index * 40} y="348" textAnchor="middle">
                  {FILES[side === "white" ? index : 7 - index]}
                </text>
                <text x="7" y={40 + index * 40} textAnchor="middle">
                  {side === "white" ? 8 - index : index + 1}
                </text>
              </g>
            ))}
          </svg>
          <figcaption
            aria-live={compact ? undefined : "polite"}
            aria-atomic={compact ? undefined : true}
          >
            <span className="opening-diagram-line">{line}</span>
            {/* A card keeps the caption to the moves and what the move hits; the page
                intro explains the arrow and the rings once. */}
            {compact && !caption ? null : (
              <span className="opening-diagram-legend">
                {compact ? null : (
                  <span className="opening-diagram-key">
                    <span className="opening-diagram-arrow-key" aria-hidden="true">
                      →
                    </span>
                    Your move
                  </span>
                )}
                {!compact && ideaSquares.length > 0 ? (
                  <span className="opening-diagram-key">
                    <span className="opening-diagram-ring-key" aria-hidden="true" />
                    Idea squares: {ideaSquares.join(", ")}
                  </span>
                ) : null}
                {caption ? (
                  <span className="opening-diagram-key">
                    <span
                      className={
                        clash.hits.length || clash.checks.length
                          ? "opening-diagram-clash-key"
                          : "opening-diagram-capture-key"
                      }
                      aria-hidden="true"
                    />
                    {caption}
                  </span>
                ) : null}
              </span>
            )}
          </figcaption>
        </figure>
        <ol
          className="opening-move-steps"
          data-steps={guide.steps.length}
          aria-label={`${side === "white" ? "White" : "Black"}’s first ${moveCount} moves`}
        >
          {guide.steps.map((item, index) => {
            const stepLabel = `${index + 1}${side === "white" ? "." : "…"} ${
              guide.line[index * 2 + (side === "white" ? 0 : 1)]
            }`;
            return (
              <li key={index}>
                <button
                  type="button"
                  aria-pressed={step === index}
                  aria-label={
                    compact
                      ? `${title}, ${stepLabel}. ${item.title}. ${item.explanation}`
                      : undefined
                  }
                  onClick={() => setStep(index)}
                  className="opening-move-step"
                >
                  <span className="opening-move-step-heading">
                    <span className="opening-move-number">{stepLabel}</span>
                    {compact ? null : <span>{item.title}</span>}
                  </span>
                  {compact ? null : (
                    <span className="opening-move-explanation">{item.explanation}</span>
                  )}
                </button>
              </li>
            );
          })}
        </ol>
        {compact ? (
          <p className="opening-card-step">
            <span className="opening-card-step-title">{lesson.title}</span>
            {lesson.explanation}
          </p>
        ) : null}
      </div>
    </section>
  );
}
